import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { BackButton } from '../components/BackButton';
import { paths } from '../navigation/paths';
import { readTablePending } from '../tableSession';

export function PlayerViewPage() {
  const navigate = useNavigate();
  const {
    playerId,
    playerName,
    setPlayerName,
    activeGames,
    pendingRequest,
    handlePlayerNameSubmit,
    handlePlayerGroupSubmit,
    fetchActiveGames,
    handleJoinGameRequest,
    handleCancelRequest,
    handlePlayerLogout,
  } = useApp();

  const [companions, setCompanions] = useState<string[]>([]);
  const startAdventure = () => {
    const names = [playerName, ...companions];
    if (companions.length === 0) handlePlayerNameSubmit();
    else handlePlayerGroupSubmit(names);
  };

  if (!playerId) {
    return (
      <div className="player-view-epic">
        <nav className="sticky-nav">
          <div className="nav-brand">
            <span className="nav-icon">⚔️</span>
            <span className="nav-title">Ralfs Abenteuer</span>
          </div>
          <div className="nav-buttons">
            <button className="nav-btn player-btn active" aria-label="Spieler" onClick={() => navigate(paths.play)}>
              <span className="btn-icon" aria-hidden>⚔️</span>
              <span className="btn-label">Spieler</span>
            </button>
            <button className="nav-btn admin-btn" aria-label="Spielleiter" onClick={() => navigate(paths.login)}>
              <span className="btn-icon" aria-hidden>👑</span>
              <span className="btn-label">Spielleiter</span>
            </button>
          </div>
        </nav>

        <div className="player-registration-epic">
          <div className="epic-overlay"></div>
          <div className="registration-container-epic">
            <div className="crown-ornament">👑</div>
            <h1 className="epic-title-form">Willkommen, Abenteurer!</h1>
            <div className="header-ornament">═══════ ✦ ═══════</div>
            <p className="registration-subtitle-epic">
              {companions.length > 0
                ? 'Diese Helden teilen sich das Gerät und treten gemeinsam bei'
                : 'Gib deinen Heldennamen ein, um dem epischen Abenteuer beizutreten'}
            </p>

            <div className="name-input-form-epic">
              <label htmlFor="playerName">Dein Heldenname:</label>
              <input
                id="playerName"
                type="text"
                value={playerName}
                onChange={(e) => setPlayerName(e.target.value)}
                placeholder="z.B. Aragorn der Tapfere"
                className="epic-input"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') startAdventure();
                }}
              />
              {companions.map((companion, index) => (
                <div key={index} className="companion-name-row">
                  <label htmlFor={`companion-${index}`}>Weiterer Held:</label>
                  <input
                    id={`companion-${index}`}
                    type="text"
                    value={companion}
                    onChange={(e) => setCompanions((current) => current.map((entry, entryIndex) => (entryIndex === index ? e.target.value : entry)))}
                    placeholder="z.B. Legolas"
                    className="epic-input"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') startAdventure();
                    }}
                  />
                  <button
                    type="button"
                    className="cta-button secondary"
                    onClick={() => setCompanions((current) => current.filter((_, entryIndex) => entryIndex !== index))}
                  >
                    Entfernen
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="cta-button secondary"
                onClick={() => setCompanions((current) => [...current, ''])}
              >
                Weiteren Helden auf diesem Gerät
              </button>
              <button className="cta-button primary" onClick={startAdventure}>
                <span className="cta-icon">⚔️</span>
                Abenteuer beginnen
              </button>
            </div>

            <BackButton fallback={paths.landing} className="cta-button secondary back-button-epic">
              <span className="cta-icon">←</span>
              Zurück<span className="btn-extra"> zur Startseite</span>
            </BackButton>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="player-view-epic">
      <nav className="sticky-nav">
        <div className="nav-brand">
          <span className="nav-icon">⚔️</span>
          <span className="nav-title">Ralfs Abenteuer</span>
        </div>
        <div className="nav-buttons">
          <button className="nav-btn player-btn active" aria-label="Spieler" onClick={() => navigate(paths.play)}>
            <span className="btn-icon" aria-hidden>⚔️</span>
            <span className="btn-label">Spieler</span>
          </button>
          <button className="nav-btn admin-btn" aria-label="Spielleiter" onClick={() => navigate(paths.login)}>
            <span className="btn-icon" aria-hidden>👑</span>
            <span className="btn-label">Spielleiter</span>
          </button>
        </div>
      </nav>

      {pendingRequest && (
        <div className="pending-overlay-epic">
          <div className="pending-content-epic">
            <div className="spinner-epic">⏳</div>
            <h2>Anfrage ausstehend</h2>
            <div className="header-ornament-small">═══════ ✦ ═══════</div>
            <p>
              {readTablePending().length > 1
                ? <>Anfrage für <strong>{readTablePending().map((entry) => entry.playerName).join(', ')}</strong> an <strong>"{pendingRequest.gameName}"</strong>.</>
                : <>Du hast eine Anfrage an <strong>"{pendingRequest.gameName}"</strong> gesendet.</>}
            </p>
            <p className="waiting-text">Warte auf die Bestätigung des Spielleiters...</p>
            <button className="cta-button secondary" onClick={handleCancelRequest}>
              Anfrage abbrechen
            </button>
          </div>
        </div>
      )}

      <div className="player-content-epic">
        <header className="player-header-epic">
          <div>
            <h1 className="player-title-epic">⚔️ Hallo, {playerName}!</h1>
            <div className="header-ornament-small">═══════ ✦ ═══════</div>
            <p className="subtitle-epic">Wähle ein Spiel zum Beitreten</p>
          </div>
          <div className="header-buttons">
            <BackButton fallback={paths.landing} className="cta-button secondary">
              ← Zurück
            </BackButton>
            <button className="cta-button secondary" onClick={handlePlayerLogout}>
              Ausloggen
            </button>
          </div>
        </header>

        <section className="active-games-section-epic">
          <div className="section-header">
            <div className="header-ornament">═══════ ✦ ═══════</div>
            <h2 className="section-title-epic">🎯 Aktive Spiele</h2>
            <div className="header-ornament">═══════ ✦ ═══════</div>
          </div>
          {activeGames.length === 0 ? (
            <div className="no-games-epic">
              <div className="no-games-icon">🏰</div>
              <p>Derzeit sind keine Spiele verfuegbar.</p>
              <p>Warte, bis der Spielleiter ein neues Spiel erstellt.</p>
            </div>
          ) : (
            <div className="games-grid-epic">
              {activeGames.map(game => (
                <div key={game.id} className="game-card-epic">
                  <div className="game-card-header">
                    <h3>{game.name}</h3>
                  </div>
                  <div className="game-info-epic">
                    <span>👥 {game.current_players || 0}/{game.max_players} Spieler</span>
                    <span>🎮 {game.is_team_game ? `Team (${game.team_size})` : 'Solo'}</span>
                    <span className={`status ${game.status}`}>
                      {game.status === 'waiting' ? '🟢 Offen' : '🔴 Voll'}
                    </span>
                  </div>
                  <button
                    className={`cta-button ${(game.current_players || 0) >= game.max_players ? 'disabled' : 'primary'}`}
                    onClick={() => handleJoinGameRequest(game.id, game.name)}
                    disabled={!!pendingRequest || (game.current_players || 0) >= game.max_players}
                  >
                    {(game.current_players || 0) >= game.max_players ? '🔒 Voll' : '📨 Beitreten'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>

        <div className="refresh-button-container">
          <button className="cta-button secondary" onClick={fetchActiveGames}>
            🔄 Aktualisieren
          </button>
        </div>
      </div>
    </div>
  );
}
